<template>
  <!-- The latest things done across every organization, newest first: who did
       what, in which organization and app, and how long ago. Each entry is
       worded by the backend; the quoted names in it are shown in bold. -->
  <ol class="ra" :class="{ 'is-refreshing': loading }" aria-label="Recent activity">
    <li v-for="entry in entries" :key="entry.id" class="ra-item">
      <span class="ra-icon" :class="`ra-tone--${actionOf(entry).tone}`" aria-hidden="true">
        <i class="pi" :class="actionOf(entry).icon"></i>
      </span>

      <div class="ra-body">
        <p class="ra-text">
          <span v-if="actorName(entry)" class="ra-actor">{{ actorName(entry) }}</span>
          <template v-for="(part, index) in descriptionParts(sentenceOf(entry))" :key="index">
            <strong v-if="part.name" class="ra-name">{{ part.text }}</strong>
            <template v-else>{{ part.text }}</template>
          </template>
        </p>

        <p class="ra-meta">
          <button
            v-if="entry.organization"
            type="button"
            class="ra-org"
            :title="`Open the activity log of ${entry.organization.name}`"
            @click="$emit('organization', entry.organization)"
          >
            <i class="pi pi-building" aria-hidden="true"></i>
            <span>{{ entry.organization.name }}</span>
          </button>
          <span v-if="sourceOf(entry)" class="ra-source" :class="`ra-source--${sourceOf(entry).value}`" :title="sourceOf(entry).title">
            <img v-if="sourceOf(entry).brand" :src="BRAND_MARKS[sourceOf(entry).brand]" alt="" class="ra-source__mark" />
            <i v-else class="pi pi-link" aria-hidden="true"></i>
            <span>{{ sourceOf(entry).label }}</span>
          </span>
          <time class="ra-time" :datetime="timeOf(entry).iso" :title="timeOf(entry).full">{{ timeAgo(entry.createdAt) }}</time>
        </p>
      </div>
    </li>
  </ol>
</template>
<script>
import chabadDigitalMark from '@/assets/images/brands/chabad-digital-icon.png';
import screenkodeMark from '@/assets/images/logo-icon.png';
import { activityAction, activitySource, descriptionParts, exactTime } from '@/utils/activity';
import { timeAgo } from '@/utils/display';

const BRAND_MARKS = { chabad_digital: chabadDigitalMark, screenkode: screenkodeMark };

const capitalizeWords = (text) => String(text || '').replace(/(^|[\s-])(\S)/g, (_, gap, letter) => gap + letter.toUpperCase());

export default {
  name: 'RecentActivity',
  props: {
    entries: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
  },
  emits: ['organization'],
  data() {
    return { BRAND_MARKS };
  },
  methods: {
    timeAgo,
    descriptionParts,
    actionOf(entry) {
      return activityAction(entry.action);
    },
    sourceOf(entry) {
      return activitySource(entry.source, entry.sourceHost);
    },
    actorName(entry) {
      if (!entry.actorName) return '';
      return entry.actorType === 'user' ? capitalizeWords(entry.actorName) : entry.actorName;
    },
    // The description follows the actor's name; with no actor it is its own
    // sentence, starting with a capital.
    sentenceOf(entry) {
      const text = String(entry.description || '');
      return entry.actorName ? ` ${text}` : text.charAt(0).toUpperCase() + text.slice(1);
    },
    timeOf(entry) {
      return exactTime(entry.createdAt);
    },
  },
};
</script>
<style scoped>
.ra {
  margin: 0;
  padding: 0;
  list-style: none;
  transition: opacity .15s ease;
}

.ra.is-refreshing {
  opacity: 0.6;
}

.ra-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 11px 8px;
  border-bottom: 1px solid var(--color-divider);
  list-style: none;
}

.ra-item:last-child {
  border-bottom: 0;
}

.ra-icon {
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
}

.ra-icon .pi {
  font-size: 0.8rem;
}

.ra-body {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 5px;
  min-width: 0;
}

.ra-text {
  margin: 0;
  color: var(--color-text-primary);
  font-size: 0.85rem;
  line-height: 1.45;
  overflow-wrap: anywhere;
}

.ra-actor,
.ra-name {
  font-weight: 600;
}

.ra-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 12px;
  margin: 0;
}

.ra-org {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  min-width: 0;
  max-width: 100%;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--color-text-secondary);
  font: inherit;
  font-size: 0.76rem;
  font-weight: 500;
  cursor: pointer;
}

.ra-org span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ra-org .pi {
  color: var(--color-text-muted);
  font-size: 0.7rem;
}

.ra-org:hover {
  color: var(--color-primary-hover);
  text-decoration: underline;
}

.ra-org:focus-visible {
  outline: none;
  border-radius: var(--radius-sm);
  box-shadow: var(--shadow-focus);
}

/* Where it was done: each brand's app with its mark. */
.ra-source {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 20px;
  padding: 0 8px 0 5px;
  border: 1px solid transparent;
  border-radius: var(--radius-pill);
  font-size: 0.68rem;
  font-weight: 700;
  white-space: nowrap;
  cursor: default;
}

.ra-source__mark {
  flex: none;
  width: auto;
  height: 12px;
}

.ra-source .pi {
  font-size: 0.6rem;
}

.ra-source--chabad_digital {
  background: #EEF1FF;
  border-color: #D5DCFF;
  color: #3347D6;
}

.ra-source--screenkode {
  background: var(--color-bg-canvas);
  border-color: var(--color-border-strong);
  color: #1F2330;
}

.ra-source--local,
.ra-source--other {
  padding-left: 7px;
  background: #F1F2F5;
  color: #5B6170;
}

.ra-time {
  color: var(--color-text-muted);
  font-size: 0.72rem;
  white-space: nowrap;
}

/* One colour per kind of action, the same as the activity log's. */
.ra-tone--green  { background: #E8F8F0; color: #0E9F6E; }
.ra-tone--indigo { background: var(--color-primary-soft); color: var(--color-primary-hover); }
.ra-tone--amber  { background: #FFF4E5; color: #C2680B; }
.ra-tone--red    { background: #FEECEC; color: #C81E1E; }
.ra-tone--slate  { background: #F1F2F5; color: #5B6170; }
.ra-tone--violet { background: #F3EEFE; color: #7C3AED; }
.ra-tone--sky    { background: #DBEAFE; color: #1D4ED8; }
</style>
